import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface PanopticonStepDetail {
  id: string;
  step_id: string;
  sequence: number;
  step_type: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error_message: string | null;
}

export interface PanopticonTaskOutcome {
  task_id: string;
  coverage_id: string | null;
  matter_id: string | null;
  task_name: string;
  task_type: string;
  terminal_revision: number;
  last_terminal_at: string;
  evaluated_at: string;
  verdict: string;
  has_defects: boolean;
  current_outcome: Record<string, unknown>;
  history: Record<string, unknown>[];
  created_at: string;
  modified_at: string;
}

export interface PanopticonRunOutcome {
  run_id: string;
  coverage_id: string | null;
  matter_id: string | null;
  agent_type: string;
  workflow_id: string;
  workflow_run_id: string | null;
  status: string;
  review_status: string | null;
  evaluated_at: string;
  verdict: string;
  has_defects: boolean;
  current_outcome: Record<string, unknown>;
  history: Record<string, unknown>[];
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  modified_at: string;
  steps?: PanopticonStepDetail[];
}

export interface PanopticonCoverageOutcome {
  coverage_id: string;
  matter_id: string | null;
  matter_name: string | null;
  coverage_status: string | null;
  coverage_category: string | null;
  latest_run_id: string | null;
  evaluated_at: string;
  health_status: string;
  needs_attention: boolean;
  current_outcome: Record<string, unknown>;
  history: Record<string, unknown>[];
  schema_patch_proposals: Record<string, unknown>[];
  created_at: string;
  modified_at: string;
  task_outcomes?: PanopticonTaskOutcome[];
  run_outcomes?: PanopticonRunOutcome[];
}

export interface PanopticonPattern {
  id: string;
  spec_id?: string;
  tag_kind?: string;
  tag_slug?: string;
  tag_display_name?: string;
  tag_description?: string | null;
  reason_namespace?: string;
  defect_type?: string;
  reason_bucket?: string;
  reason_code?: string;
  window_name: string;
  window_started_at: string;
  window_ended_at: string;
  count: number;
  sample_subject_ids: string[];
  sample_findings?: {
    subject_id: string;
    subject_kind: string;
    summary: string | null;
    reasoning: string | null;
    verdict: string | null;
  }[];
  sample_outcomes?: Record<string, unknown>[];
}

export interface PanopticonBackfillRun {
  id: string;
  scope: string;
  status: string;
  window_start_at: string;
  window_end_at: string;
  workflow_id: string | null;
  triggered_by_user_id: string | null;
  total_coverages: number;
  processed_coverages: number;
  total_snapshots: number;
  processed_snapshots: number;
  error: string | null;
  result: Record<string, unknown>;
  created_at: string;
  modified_at: string;
}

export interface PanopticonCoverageListResponse {
  coverages: PanopticonCoverageOutcome[];
}

export interface PanopticonCoverageDetailResponse {
  coverage: PanopticonCoverageOutcome;
}

export interface PanopticonTaskDetailResponse {
  task: PanopticonTaskOutcome;
}

export interface PanopticonRunDetailResponse {
  run: PanopticonRunOutcome;
}

export interface PanopticonPatternsResponse {
  patterns: PanopticonPattern[];
}

export interface PanopticonBackfillListResponse {
  backfills: PanopticonBackfillRun[];
}

export interface PanopticonBackfillDetailResponse {
  backfill: PanopticonBackfillRun;
}

export interface CreatePanopticonBackfillRequest {
  window_start: string;
  window_end: string;
  coverage_ids?: string[];
  matter_ids?: string[];
  dry_run?: boolean;
  inline?: boolean;
  use_read_replica?: boolean;
  max_concurrent_coverages?: number;
}

export const panopticonApiClient = createApi({
  reducerPath: 'panopticonApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Panopticon'] as const,
  endpoints: (builder) => ({
    getPanopticonCoverages: builder.query<
      PanopticonCoverageListResponse,
      {
        health_status?: string;
        matter_id?: string;
        coverage_id?: string;
      } | void
    >({
      query: (params) => ({
        url: 'api/admin/panopticon/coverages/',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: (result) => [
        { type: 'Panopticon', id: 'COVERAGES' },
        ...(result?.coverages ?? []).map((coverage) => ({
          type: 'Panopticon' as const,
          id: `coverage:${coverage.coverage_id}`,
        })),
      ],
    }),
    getPanopticonCoverageDetail: builder.query<
      PanopticonCoverageDetailResponse,
      string
    >({
      query: (coverageId) => ({
        url: `api/admin/panopticon/coverages/${coverageId}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, coverageId) => [
        { type: 'Panopticon', id: `coverage:${coverageId}` },
      ],
    }),
    getPanopticonTaskDetail: builder.query<PanopticonTaskDetailResponse, string>({
      query: (taskId) => ({
        url: `api/admin/panopticon/tasks/${taskId}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, taskId) => [
        { type: 'Panopticon', id: `task:${taskId}` },
      ],
    }),
    getPanopticonRunDetail: builder.query<PanopticonRunDetailResponse, string>({
      query: (runId) => ({
        url: `api/admin/panopticon/runs/${runId}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, runId) => [
        { type: 'Panopticon', id: `run:${runId}` },
      ],
    }),
    getPanopticonPatterns: builder.query<PanopticonPatternsResponse, void>({
      query: () => ({
        url: 'api/admin/panopticon/patterns/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Panopticon', id: 'PATTERNS' }],
    }),
    getPanopticonBackfills: builder.query<PanopticonBackfillListResponse, void>({
      query: () => ({
        url: 'api/admin/panopticon/backfills/',
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'Panopticon', id: 'BACKFILLS' },
        ...(result?.backfills ?? []).map((backfill) => ({
          type: 'Panopticon' as const,
          id: `backfill:${backfill.id}`,
        })),
      ],
    }),
    createPanopticonBackfill: builder.mutation<
      PanopticonBackfillDetailResponse,
      CreatePanopticonBackfillRequest
    >({
      query: (body) => ({
        url: 'api/admin/panopticon/backfills/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Panopticon', id: 'BACKFILLS' },
        { type: 'Panopticon', id: 'COVERAGES' },
      ],
    }),
    cancelPanopticonBackfill: builder.mutation<{ cancelled: boolean }, string>({
      query: (runId) => ({
        url: `api/admin/panopticon/backfills/${runId}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, runId) => [
        { type: 'Panopticon', id: 'BACKFILLS' },
        { type: 'Panopticon', id: `backfill:${runId}` },
      ],
    }),
  }),
});

export const {
  useCancelPanopticonBackfillMutation,
  useCreatePanopticonBackfillMutation,
  useGetPanopticonBackfillsQuery,
  useGetPanopticonCoverageDetailQuery,
  useGetPanopticonCoveragesQuery,
  useGetPanopticonPatternsQuery,
  useGetPanopticonRunDetailQuery,
  useGetPanopticonTaskDetailQuery,
} = panopticonApiClient;
